import logo from '/images/logo.png';
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  IconButton, 
  useTheme, 
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  useMediaQuery,
  Container,
  Divider,
  Chip
} from '@mui/material';
import { 
  FaPhoneAlt, 
  FaClock, 
  FaMapMarkerAlt, 
  FaFacebookF, 
  FaLinkedinIn, 
  FaInstagram, 
  FaChevronDown,
  FaBars,
  FaTimes,
  FaChevronRight
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const NavBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navRoutes = {
    'Our Founder': '/our-founder',
    'Society': '/welfare-society',
    'History': '/our-history',
    'Aims & Objectives': '/aims-objectives',
    'School Curriculum': '/school-curriculum',
    'Facilities': '/school-facilities',
    'Mandatory Public Disclosure': '/school-disclosure',
    'E-Prospectus': '/e-prospectus',
    'SMC': '/smc',
    'Contact Us': '/contact-us',
    "Director/Admin's Message": '/messages/director',
    "Principal's Message": '/messages/principal',
    "Vice Principal's Message": '/messages/vice-principal',
    'Berry Bees': '/berry-bees',
    'Excursion & Educational Visits': '/excursion',
    'Kids Corner': '/kids-corner',
    'Sports Activity': '/sports-activities',
    'School Calendar': '/school-calendar',
    'Fee Structure': '/school-fee-structure',
    'Dress Rules': '/dress-rules',
    'Book-List 2025–26': '/book-list',
    'FORMS': '/forms',
    'Toppers Class X': '/toppers/class-x',
    'Toppers Class XII': '/toppers/class-xii',
    'Discipline': '/rules/discipline',
    'Admission': '/rules/admission',
    'Withdrawal': '/rules/withdrawal',
    'Uniform': '/rules/uniform',
    'Fee Rules': '/rules/fee',
  };

  const navigationItems = [
    { label: 'Home', path: '/' },
    {
      label: 'About',
      subItems: [
        'Our Founder', 'Society', 'History', 'Aims & Objectives',
        'School Curriculum', 'Facilities', 'Mandatory Public Disclosure',
        'E-Prospectus', 'SMC', 'Contact Us'
      ]
    },
    {
      label: 'Messages',
      subItems: [
        "Director/Admin's Message",
        "Principal's Message",
        "Vice Principal's Message"
      ]
    },
    {
      label: 'Students',
      subItems: [
        'Berry Bees', 'Excursion & Educational Visits', 'Kids Corner',
        'Sports Activity', 'School Calendar', 'Fee Structure',
        'Dress Rules', 'Book-List 2025–26', 'FORMS'
      ]
    },
    {
      label: 'Notice',
      isSpecial: true,
      noticeItems: [
        {
          title: 'Annual Day Celebration',
          datePosted: '05 May 2025',
          validUntil: '25 May 2025',
          info: 'Annual day celebration to be held on 24th May 2025 at school auditorium.',
          isNew: true
        },
        {
          title: 'Admission Open for 2025-26',
          datePosted: '01 May 2025',
          validUntil: '30 Jun 2025',
          info: 'Admissions open for all classes. Limited seats available.',
          isUrgent: true
        },
        {
          title: 'Examination Schedule',
          datePosted: '28 Apr 2025',
          validUntil: '20 May 2025',
          info: 'Final examination schedule for all classes has been released.'
        },
        {
          title: 'Parent-Teacher Meeting',
          datePosted: '26 Apr 2025',
          validUntil: '15 May 2025',
          info: 'PTM scheduled for 14th May for classes VI-XII.'
        },
        {
          title: 'Summer Camp Registration',
          datePosted: '24 Apr 2025',
          validUntil: '25 May 2025',
          info: 'Register for summer activities including sports, arts, and coding.'
        }
      ]
    },
    {
      label: 'Toppers',
      subItems: ['Toppers Class X', 'Toppers Class XII']
    },
    {
      label: 'Rules',
      subItems: ['Discipline', 'Admission', 'Withdrawal', 'Uniform', 'Fee Rules']
    },
    { label: 'Office Bearers', path: '/office-bearers' }
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileItemClick = (label: string) => {
    if (expandedMobile === label) {
      setExpandedMobile(null);
    } else {
      setExpandedMobile(label);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
    setExpandedMobile(null);
  };

  // Desktop Navigation Component
  const DesktopNav = () => (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {navigationItems.map(({ label, path, subItems, noticeItems, isSpecial }) => (
        <Box
          key={label}
          onMouseEnter={() => setIsHovered(label)}
          onMouseLeave={() => setIsHovered(null)}
          onClick={() => path && navigate(path)}
          sx={{
            position: 'relative',
            zIndex: 10,
            '&:hover .dropdown': {
              opacity: 1,
              transform: 'translateY(0)',
              visibility: 'visible',
            },
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.2,
              borderRadius: '30px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              background: isSpecial 
                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                : label === 'Home' 
                  ? 'rgba(255, 255, 255, 0.15)' 
                  : 'transparent',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
              },
              ...(isSpecial && {
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(255, 107, 107, 0.7)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(255, 107, 107, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(255, 107, 107, 0)' }
                }
              })
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography
                sx={{
                  fontSize: { xs: '13px', md: '14px' },
                  fontWeight: label === 'Home' ? 600 : 500,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '0.5px',
                }}
              >
                {label}
              </Typography>
              {(subItems || noticeItems) && (
                <FaChevronDown 
                  style={{ 
                    fontSize: '10px', 
                    color: 'white',
                    transition: 'transform 0.3s ease',
                    transform: isHovered === label ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} 
                />
              )}
            </Stack>
          </Box>

          {/* Enhanced Dropdown Menu */}
          {(subItems || noticeItems) && (
            <Box
              className="dropdown"
              sx={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%) translateY(15px)',
                minWidth: noticeItems ? 420 : 220,
                maxWidth: noticeItems ? 480 : 280,
                zIndex: 9999,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                py: 2,
                opacity: 0,
                visibility: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid rgba(255, 255, 255, 0.98)',
                }
              }}
            >
              {subItems && subItems.map((item, index) => (
                <Typography
                  key={item}
                  onClick={() => {
                    const route = navRoutes[item as keyof typeof navRoutes];
                    if (route) navigate(route);
                  }}
                  sx={{
                    px: 3,
                    py: 1.5,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    transition: 'all 0.3s ease',
                    borderRadius: '12px',
                    mx: 1,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateX(8px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  {item}
                </Typography>
              ))}

              {noticeItems && noticeItems.map((notice, index) => (
                <Box
                  key={notice.title}
                  sx={{
                    px: 3,
                    py: 2.5,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    mx: 1,
                    mb: 1.5,
                    background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                    border: '1px solid #e8ecf4',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      backgroundColor: '#f0f4ff',
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography
                      fontWeight={600}
                      color="primary.main"
                      sx={{ 
                        fontSize: '14px',
                        lineHeight: 1.3,
                        flex: 1,
                        mr: 1
                      }}
                    >
                      {notice.title}
                    </Typography>
                    {notice.isNew && (
                      <Chip 
                        label="NEW" 
                        size="small" 
                        sx={{ 
                          fontSize: '10px', 
                          height: '20px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          fontWeight: 600
                        }} 
                      />
                    )}
                    {notice.isUrgent && (
                      <Chip 
                        label="URGENT" 
                        size="small" 
                        sx={{ 
                          fontSize: '10px', 
                          height: '20px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          fontWeight: 600
                        }} 
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '11px',
                        color: '#666',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      📅 {notice.datePosted}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '11px',
                        color: '#666',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      ⏰ {notice.validUntil}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#555',
                      lineHeight: 1.5,
                    }}
                  >
                    {notice.info}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );

  // Mobile Navigation Drawer
  const MobileNav = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuToggle}
      sx={{
        '& .MuiDrawer-paper': {
          width: '85%',
          maxWidth: '400px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={handleMobileMenuToggle} sx={{ color: 'white' }}>
            <FaTimes />
          </IconButton>
        </Stack>

        <List>
          {navigationItems.map(({ label, path, subItems, noticeItems }) => (
            <Box key={label}>
              <ListItemButton
                onClick={() => {
                  if (path) {
                    handleNavigation(path);
                  } else {
                    handleMobileItemClick(label);
                  }
                }}
                sx={{
                  borderRadius: '12px',
                  mb: 1,
                  backgroundColor: expandedMobile === label ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <ListItemText
                  primary={label}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 500,
                      fontSize: '16px',
                    },
                  }}
                />
                {(subItems || noticeItems) && (
                  <FaChevronRight
                    style={{
                      fontSize: '12px',
                      transition: 'transform 0.3s ease',
                      transform: expandedMobile === label ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                )}
              </ListItemButton>

              <Collapse in={expandedMobile === label} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {subItems && subItems.map((item) => (
                    <ListItemButton
                      key={item}
                      onClick={() => {
                        const route = navRoutes[item as keyof typeof navRoutes];
                        if (route) handleNavigation(route);
                      }}
                      sx={{
                        pl: 4,
                        borderRadius: '8px',
                        mx: 1,
                        my: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={item}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '14px',
                            opacity: 0.9,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}

                  {noticeItems && noticeItems.map((notice) => (
                    <Box
                      key={notice.title}
                      sx={{
                        mx: 2,
                        my: 1,
                        p: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        sx={{ fontSize: '14px', mb: 1 }}
                      >
                        {notice.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          opacity: 0.8,
                          lineHeight: 1.4,
                        }}
                      >
                        {notice.info}
                      </Typography>
                    </Box>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      <Box
        sx={{
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          transition: 'all 0.3s ease',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          backgroundColor: scrolled ? 'rgba(102, 126, 234, 0.95)' : 'transparent',
          boxShadow: scrolled ? '0 8px 32px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        {/* Top Contact Bar - Hidden on mobile */}
        {!isTablet && (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              py: 1.5,
              px: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Container maxWidth="xl">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {/* Contact Info */}
                <Stack direction="row" spacing={4}>
                  {[
                    { icon: <FaPhoneAlt />, text: '05946- 292491' },
                    { icon: <FaClock />, text: 'Mon - Sat, 9 AM - 5 PM' },
                  ].map((item, idx) => (
                    <Stack 
                      key={idx} 
                      direction="row" 
                      spacing={1} 
                      alignItems="center"
                      sx={{ color: 'white', opacity: 0.9 }}
                    >
                      <Box sx={{ fontSize: '12px' }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 400 }}>
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* Social Icons */}
                <Stack direction="row" spacing={1}>
                  {[FaFacebookF, FaLinkedinIn, FaInstagram].map((Icon, idx) => (
                    <IconButton 
                      key={idx} 
                      size="small" 
                      sx={{ 
                        color: 'white',
                        fontSize: '14px',
                        padding: '8px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          transform: 'translateY(-2px) scale(1.1)',
                        }
                      }}
                    >
                      <Icon />
                    </IconButton>
                  ))}
                </Stack>
              </Stack>
            </Container>
          </Box>
        )}

        {/* Main Navigation */}
        <Box sx={{ py: { xs: 1.5, md: 2 }, px: 2 }}>
          <Container maxWidth="xl">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Logo Section */}
              <Stack 
                direction="row" 
                spacing={{ xs: 1.5, md: 2 }} 
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <Box 
                  component="img" 
                  src={logo} 
                  alt="Logo" 
                  sx={{ 
                    height: { xs: 45, md: 55 },
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(5deg)',
                    }
                  }} 
                />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'serif',
                      fontWeight: '700',
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      color: 'white',
                      textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                      lineHeight: 1.2,
                      letterSpacing: '1px',
                    }}
                  >
                    EVER GREEN
                  </Typography>
                  <Typography 
                    sx={{ 
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.9)',
                      letterSpacing: '0.5px',
                      textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    SENIOR SECONDARY SCHOOL
                  </Typography>
                </Box>
              </Stack>

              {/* Desktop Navigation */}
              {!isMobile && <DesktopNav />}

              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <FaBars />
                </IconButton>
              )}

              {/* Address Info - Hidden on tablet and mobile */}
              {!isMobile && (
                <Stack 
                  direction="row" 
                  spacing={1} 
                  alignItems="center" 
                  sx={{ 
                    color: 'white',
                    display: { xs: 'none', lg: 'flex' }
                  }}
                >
                  <FaMapMarkerAlt style={{ fontSize: '14px', opacity: 0.9 }} />
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, opacity: 0.9 }}>
                      Sri Ganga Vihar Colony
                    </Typography>
                    <Typography sx={{ fontSize: '11px', opacity: 0.8 }}>
                      Beri Parao, Nainital (UK)
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Navigation Drawer */}
      <MobileNav />
    </>
  );
};

export default NavBar;